import { z } from 'zod';
import { v1PaginationQuerySchema, v1PaginationSchema } from './pagination';
import { csvParam, v1SortOrderSchema } from './query';
import { v1UserRefSchema } from './actor';
import { v1PlatformIdFilterDescription, v1PlatformSchema } from './vocabulary';
/**
 * Public Alerts resource for `/api/v1` (KIR-4376).
 *
 * Every field here is frozen for the life of `v1`: it may gain optional
 * siblings, never lose or rename one. That is why this file restates the shape
 * instead of re-exporting `contracts/alerts.ts` — the internal grouped-alert
 * contract carries roughly forty fields that exist to drive one specific table
 * UI, several of them flag-gated and several more (match context, entropy,
 * endpoint identifiers) deliberately not customer-visible. Anything not listed
 * below cannot leak through the DTO mapper, because the mapper never spreads.
 */
/** Public severity vocabulary. A closed set; matches the product's four levels. */
export const v1AlertSeveritySchema = z
    .enum(['low', 'medium', 'high', 'critical'])
    .meta({
    id: 'V1AlertSeverity',
    title: 'Alert severity',
    description: "How serious the finding is. The product's four levels.",
});
/**
 * What the system did about the alert. `unknown` covers findings ingested
 * before outcome reporting existed, and is a real value rather than a null so
 * a caller's switch stays exhaustive.
 */
export const v1AlertOutcomeSchema = z
    .enum(['prevented', 'detected', 'unknown'])
    .meta({
    id: 'V1AlertOutcome',
    title: 'Alert outcome',
    description: 'What the system did about the alert. `prevented` means the action was blocked; `detected` means it was observed and reported. `unknown` covers findings ingested before outcome reporting existed — a real value rather than a null, so an exhaustive switch stays exhaustive.',
});
/**
 * Suppression state collapsed to one value for callers that only need to know
 * whether an alert is live.
 *
 * Precedence when an alert is both muted and excluded: `excluded` wins, because
 * exclusion is the stronger statement (it suppresses future enforcement, not
 * just notification). The `mute` and `exclusion` objects are still both present
 * on the alert, so nothing is lost by the collapse.
 *
 * **Named `suppressionState`, not `status`** (KIR-4432). The Alerts screen
 * already spends the word "Status" on the KIR-4440 feedback verdict —
 * `verified` / `false_positive` / … — which this API publishes as
 * {@link v1AlertFeedbackSchema}. Two unrelated vocabularies under one word is
 * the single most confusing thing this contract could do, so the field is named
 * for what it actually reports and the screen's word is left to the screen's
 * concept.
 */
export const v1AlertSuppressionStateSchema = z
    .enum(['active', 'muted', 'excluded'])
    .meta({
    id: 'V1AlertSuppressionState',
    title: 'Alert suppression state',
    description: 'Whether the alert is live, muted, or excluded — the two suppression mechanisms collapsed to one value. When an alert is both muted and excluded, `excluded` wins: exclusion is the stronger statement. The `mute` and `exclusion` objects are still both present, so nothing is lost by the collapse. This is **not** the Kirin app\'s "Status" column, which shows the triage verdict published here as `feedback.status`.',
});
/** Which alerts a list call should return. Mirrors {@link v1AlertSuppressionStateSchema} plus `all`. */
// Deliberately unnamed, unlike its sibling enums: this one is only ever a query
// parameter, and a named schema is emitted as a bare `$ref`, which carries no
// room for the use-site description. Inlining keeps the explanation on the
// parameter, where the reader of the docs actually meets it.
export const v1AlertSuppressionStateFilterSchema = z.enum([
    'active',
    'suppressed',
    'all',
]);
/**
 * The triage verdict a person recorded against an alert.
 *
 * **Restated here rather than re-exported from `contracts/alerts.ts`**, for the
 * reason this whole file exists: the internal enum is free to grow, and a value
 * added there would silently widen a vocabulary that is frozen for the life of
 * `v1`, breaking every client that switches exhaustively. The DTO narrows
 * through an allow-list, so a new internal verdict surfaces as `unverified`
 * rather than escaping the enum.
 *
 * **Hyphenated, not the internal `false_positive` / `true_positive`.** Those are
 * stored snake_case; `NAMING.md` forbids publishing an internal slug, and the
 * DTO absorbs the difference in both directions — the same treatment MCP gives
 * `running`/`idle` and Shadow AI gives `code_assist`.
 */
export const v1AlertFeedbackStatusSchema = z
    .enum([
    'unverified',
    'verified',
    'false-positive',
    'true-positive',
    'unknown',
])
    .meta({
    id: 'V1AlertFeedbackStatus',
    title: 'Alert feedback status',
    description: 'The triage verdict a person recorded against an alert. `unverified` is the state every alert starts in. Informational only: a verdict does not mute, exclude, or change severity.',
});
/**
 * The triage verdict a manager or owner set on the alert, plus the free-text
 * comment beside it — the Alerts screen's "Status" and "Comment" columns.
 *
 * Published as one nested object rather than flat siblings for the same reason
 * `mute` is: the value, who set it and when travel together, and the internal
 * row carries them as five flat columns per field that a caller would otherwise
 * have to reassemble.
 *
 * **Null when the `alert_feedback` feature is not enabled for the
 * organization** — the same nullable-object convention `mute` and `exclusion`
 * already use for their flags. A nullable object is one shape a client must
 * handle; an optional one is two.
 */
export const v1AlertFeedbackSchema = z
    .object({
    status: v1AlertFeedbackStatusSchema.describe('The triage verdict. `unverified` is the default every alert starts at, so this is never null while feedback is enabled.'),
    statusSetAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When the verdict was last set (ISO 8601 UTC). Null while the verdict is still the default.'),
    statusSetBy: v1UserRefSchema
        .nullable()
        .describe('Who last set the verdict. Null while it is still the default, or if that user has since been deleted.'),
    comment: z
        .string()
        .nullable()
        .describe('The free-text triage note. Null until someone writes one.'),
    commentSetAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When the comment was last written (ISO 8601 UTC). Null while there is no comment.'),
    commentSetBy: v1UserRefSchema
        .nullable()
        .describe('Who last wrote the comment. Null while there is none, or if that user has since been deleted.'),
})
    .meta({
    id: 'V1AlertFeedback',
    title: 'Alert feedback',
    description: 'The triage verdict and note a person recorded against the alert — what the Kirin app shows in its "Status" and "Comment" columns. Informational only: setting a verdict does not mute, exclude, or change severity.',
    examples: [
        {
            status: 'true-positive',
            statusSetAt: '2026-08-28T11:02:15Z',
            statusSetBy: {
                id: '3fb9c1d4-7e62-4a08-9c53-2d81f0a64e7b',
                name: 'Roger Wilco',
                email: 'roger@example.com',
            },
            comment: 'Key rotated, confirmed with the owning team.',
            commentSetAt: '2026-08-28T11:04:40Z',
            commentSetBy: {
                id: '3fb9c1d4-7e62-4a08-9c53-2d81f0a64e7b',
                name: 'Roger Wilco',
                email: 'roger@example.com',
            },
        },
    ],
});
/**
 * The customer-facing explanation of a finding, in the three parts the app's
 * alert card renders.
 *
 * This is the copy a person reads on the screen, and before KIR-4432 it was
 * unreachable through the API. The alert card resolves it from the
 * `(subPolicyKey, type)` pair through the shared display registry — the same
 * lookup that produces `title` — and only falls back to the raw `description`
 * when the pair has no entry. Publishing `description` alone therefore gave a
 * customer a string the screen does not show, with no way to obtain the one it
 * does.
 *
 * Null when the finding has no registry entry; the raw `description` is the
 * whole explanation in that case, which is exactly what the app falls back to.
 */
export const v1AlertExplanationSchema = z
    .object({
    whatHappened: z
        .string()
        .describe('What Kirin observed, in one or two sentences.'),
    whyItMatters: z
        .string()
        .describe('Why the observation is a security concern.'),
    detected: z
        .string()
        .nullable()
        .describe("The specific per-finding detail, when the finding carries one that the static copy would otherwise hide — the hidden character's name and codepoint, a decoded steganographic payload, the matched rule's reason, or an AI classifier's summary. This is the app's \"Detected\" line. Null when the finding has no such detail."),
})
    .meta({
    id: 'V1AlertExplanation',
    title: 'Alert explanation',
    description: 'The finding explained the way the Kirin app explains it, in the same three parts its alert card renders.',
    examples: [
        {
            whatHappened: 'An AWS access key ID and secret were pasted into a Claude Code chat.',
            whyItMatters: 'Chat content leaves the machine. A live cloud credential in a prompt is a credential disclosed to a third party.',
            detected: 'AWS access key ID (AKIA…), 1 occurrence',
        },
    ],
});
/**
 * A user an alert was seen for, with that user's share of the occurrences.
 * Counts are scoped to the same filters the list call applied.
 */
export const v1AlertUserSchema = v1UserRefSchema
    .extend({
    occurrenceCount: z
        .number()
        .int()
        .min(0)
        .describe("This user's share of the alert's occurrences, scoped to the same filters the list call applied."),
    lastSeenAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When this user last triggered the alert (ISO 8601 UTC).'),
})
    .meta({
    id: 'V1AlertUser',
    title: 'Alert user',
    description: "A user the alert was seen for, with that user's share of the occurrences.",
    examples: [
        {
            id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
            name: 'Stella Normal',
            email: 'stella@example.com',
            occurrenceCount: 3,
            lastSeenAt: '2026-08-31T14:03:07Z',
        },
    ],
});
/**
 * Mute state. `muted` is evaluated at read time against `mutedUntil`, so an
 * expired timed mute reports `muted: false` without waiting for a sweeper.
 */
export const v1AlertMuteSchema = z
    .object({
    muted: z
        .boolean()
        .describe('Whether the mute is in force right now. Evaluated at read time against `mutedUntil`, so an expired timed mute reports `false` without waiting for a sweeper.'),
    mutedAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When the mute began (ISO 8601 UTC). Read this to tell a fresh mute from a replayed one, since both answer 200.'),
    /** Null with `muted: true` means muted indefinitely. */
    mutedUntil: z
        .string()
        .datetime()
        .nullable()
        .describe('When the mute expires (ISO 8601 UTC). Null alongside `muted: true` means muted indefinitely.'),
    /** Null when the muting user has since been deleted. */
    mutedBy: v1UserRefSchema
        .nullable()
        .describe('Who muted it. Null if that user has since been deleted.'),
})
    .meta({
    id: 'V1AlertMute',
    title: 'Mute state',
    description: 'Whether an alert is muted, until when, and by whom.',
    examples: [
        {
            muted: true,
            mutedAt: '2026-08-29T09:20:00Z',
            mutedUntil: '2026-09-30T00:00:00Z',
            mutedBy: {
                id: '3fb9c1d4-7e62-4a08-9c53-2d81f0a64e7b',
                name: 'Roger Wilco',
                email: 'roger@example.com',
            },
        },
    ],
});
/** Exclusion state. Mirror of {@link v1AlertMuteSchema}; same expiry semantics. */
export const v1AlertExclusionSchema = z
    .object({
    excluded: z
        .boolean()
        .describe('Whether the exclusion is in force right now. Same read-time evaluation as `mute.muted`.'),
    excludedAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When the exclusion began (ISO 8601 UTC).'),
    /** Null with `excluded: true` means excluded indefinitely. */
    excludedUntil: z
        .string()
        .datetime()
        .nullable()
        .describe('When the exclusion expires (ISO 8601 UTC). Null alongside `excluded: true` means excluded indefinitely.'),
    excludedBy: v1UserRefSchema
        .nullable()
        .describe('Who excluded it. Null if that user has since been deleted.'),
})
    .meta({
    id: 'V1AlertExclusion',
    title: 'Exclusion state',
    description: 'Whether an alert is excluded, until when, and by whom. Exclusion suppresses future enforcement, not just notification.',
    examples: [
        {
            excluded: false,
            excludedAt: null,
            excludedUntil: null,
            excludedBy: null,
        },
    ],
});
/**
 * One alert.
 *
 * `id` is stable for the life of the underlying finding identity, so it is safe
 * to store in an external system and use as the mute/exclusion path segment.
 *
 * `mute` / `exclusion` are `null` — not absent — when the corresponding feature
 * is not enabled for the organization. A nullable object is one shape a client
 * must handle; an optional one is two.
 */
export const v1AlertSchema = z
    .object({
    id: z
        .string()
        .describe('Stable for the life of the underlying finding, so it is safe to store in an external system. Also the path segment for the mute and exclusion endpoints.'),
    type: z
        .string()
        .describe('The stable machine identifier for the kind of finding, e.g. `auto_run` or `hidden_characters`. Use this to group or filter; use `title` to display. Note that several types can share one title — four legacy MCP types all render as "Untrusted MCP Server" — and that one type can carry two titles depending on `subPolicyKey`, so `(subPolicyKey, type)` is the pair that identifies a title.'),
    title: z
        .string()
        .describe('The finding\'s name as the Kirin app shows it — the "Title" column on the Alerts screen, e.g. "Vulnerable dependency in MCP server". Resolved from the `(subPolicyKey, type)` pair through the same display registry the app renders, so a report built on this field reads the way the product does. Falls back to a title-cased `type` for a finding with no registry entry.'),
    severity: v1AlertSeveritySchema,
    description: z
        .string()
        .describe('The raw per-finding summary recorded by the scanner. This is the detail specific to *this* finding, not the general explanation of the finding kind — for the latter, and for what the app actually renders on its alert card, read `explanation`.'),
    explanation: v1AlertExplanationSchema
        .nullable()
        .describe('The finding explained as the Kirin app explains it. Null when this finding kind has no registry entry, in which case `description` is the whole explanation.'),
    // ON THE LIST AS WELL AS THE DETAIL ROUTE, deliberately: per-kind prose
    // repeats on every row that shares a `(subPolicyKey, type)` pair. Measured
    // 2026-09: 365 chars average, 498 longest, so a `V1_MAX_PAGE_SIZE` page of
    // 100 carries ~37 KB of it and ~50 KB at worst. The whole table is 56
    // distinct strings, ~20 KB — a caller paging the full alert set refetches
    // far more prose than exists.
    //
    // Taken for CONSISTENCY, which beats the bytes here: `explanation` above is
    // per-kind and repeats on the same rows, and a caller should not have to
    // learn that one curated field is on the list and another is not.
    //
    // Considered and declined, both unavailable later without a v2: detail
    // route only, and the mapping as its own resource clients join against.
    // Revisit only if a customer reports the page weight, since dropping it
    // from the list is itself a breaking change.
    mitigation: z
        .string()
        .nullable()
        .describe('The recommended actions for this finding kind — the Kirin Security team\'s guidance, as the app shows it in its "Recommended actions" section. Prose, one paragraph, meant to be read by a person; it describes the finding KIND, so every alert of the same `(subPolicyKey, type)` pair carries identical text. Null when the recommended-actions feature is not enabled for the organization, and also when this finding kind has no guidance yet — a caller cannot distinguish the two, the same way `feedback` behaves.'),
    suppressionState: v1AlertSuppressionStateSchema,
    outcome: v1AlertOutcomeSchema,
    feedback: v1AlertFeedbackSchema
        .nullable()
        .describe('The triage verdict and note recorded against this alert, or null when alert feedback is not enabled for the organization.'),
    firstSeenAt: z
        .string()
        .datetime()
        .describe('When this alert was first observed (ISO 8601 UTC).'),
    lastSeenAt: z
        .string()
        .datetime()
        .describe('When this alert was most recently observed (ISO 8601 UTC).'),
    occurrenceCount: z
        .number()
        .int()
        .min(0)
        .describe('How many times the finding has been observed.'),
    /** Null for alerts with no file (chat content, process activity). */
    filePath: z
        .string()
        .nullable()
        .describe('Path of the file the finding is in. Null for alerts with no file, such as chat content or process activity.'),
    /** 0–100 risk score derived from severity. Null when not computed. */
    score: z
        .number()
        .int()
        .min(0)
        .max(100)
        .nullable()
        .describe('Risk score from 0 to 100, derived from severity. Null when not computed.'),
    /** Kirin policy that produced the alert. Null for legacy unmapped types. */
    policyKey: z
        .string()
        .nullable()
        .describe('The Kirin policy that produced the alert, as a stable key. Null for legacy findings that predate policy mapping.'),
    policyName: z
        .string()
        .nullable()
        .describe('That policy\'s name as the Kirin app shows it, e.g. "Secret Detection". Null whenever `policyKey` is, or when the key is one this version does not have a name for.'),
    subPolicyKey: z
        .string()
        .nullable()
        .describe('The specific rule within that policy, when the policy has several, as a stable key.'),
    subPolicyName: z
        .string()
        .nullable()
        .describe('That rule\'s name as the Kirin app shows it, e.g. "Secret File Rules". Null whenever `subPolicyKey` is, or when the key is one this version does not have a name for.'),
    /** IDEs / agent platforms this alert was seen on, sorted, may be empty. */
    platforms: z
        .array(v1PlatformSchema)
        .describe('IDEs and agent platforms the alert was seen on, sorted by identifier. May be empty. Pass `platforms[].id` to the `platform` filter.'),
    users: z
        .array(v1AlertUserSchema)
        .describe('The users this alert was seen for, with per-user occurrence counts.'),
    mute: v1AlertMuteSchema
        .nullable()
        .describe('Mute state, or null when muting is not enabled for the organization. Null rather than absent, so there is one shape to handle instead of two.'),
    exclusion: v1AlertExclusionSchema
        .nullable()
        .describe('Exclusion state, or null when exclusions are not enabled for the organization.'),
})
    .meta({
    id: 'V1Alert',
    title: 'Alert',
    description: 'One security finding raised for your organization, aggregated across every occurrence of the same underlying issue.',
    examples: [
        {
            id: '9e2aca8a1ba8912841bbf861767ac310a766435ee98324182237ba65d52b7150',
            type: 'secret-leakage',
            title: 'Secret detected in chat content',
            severity: 'critical',
            description: 'An AWS access key was sent to an AI assistant in chat content.',
            explanation: {
                whatHappened: 'An AWS access key ID and secret were pasted into a Claude Code chat.',
                whyItMatters: 'Chat content leaves the machine. A live cloud credential in a prompt is a credential disclosed to a third party.',
                detected: 'AWS access key ID (AKIA…), 1 occurrence',
            },
            // The approved guidance for this finding's mapping, verbatim. This
            // example's title is the `customPatterns` / `secret-leakage` row, so
            // this is that row's copy rather than illustrative prose.
            mitigation: 'Confirm whether the value is a real credential or a test value or placeholder. If it is real, check whether it also exists in a commit, an exported conversation, or a shared log, and revoke and reissue it if you cannot rule that out; a credential reliably blocked before transmission and not exposed elsewhere may not need rotation. If it is real, remove it from the content and keep it in your secret manager. If it is a test value or placeholder, make sure it is clearly marked as non-production.',
            suppressionState: 'active',
            outcome: 'detected',
            feedback: null,
            firstSeenAt: '2026-08-24T09:12:44Z',
            lastSeenAt: '2026-08-31T14:03:07Z',
            occurrenceCount: 3,
            filePath: null,
            score: 92,
            policyKey: 'chat-security',
            policyName: 'Chat & Tool-Call Security',
            subPolicyKey: 'secret-detection',
            subPolicyName: 'Secret Detection',
            platforms: [{ id: 'claude', name: 'Claude Code' }],
            users: [
                {
                    id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
                    name: 'Stella Normal',
                    email: 'stella@example.com',
                    occurrenceCount: 3,
                    lastSeenAt: '2026-08-31T14:03:07Z',
                },
            ],
            mute: null,
            exclusion: null,
        },
    ],
});
/** Sortable fields. Every sort is made total by an internal id tie-break, so repeated calls return the same order. */
export const v1AlertSortFieldSchema = z
    .enum(['lastSeenAt', 'firstSeenAt', 'severity', 'occurrenceCount'])
    .describe('Field to sort by. Every sort carries an internal tie-break, so repeated calls return the same order.');
/**
 * `GET /api/v1/organizations/:organizationId/alerts` query.
 *
 * Filters are the ones the matching UI screen offers (parent AC #2): severity,
 * outcome, user, file, IDE/platform, first seen, last seen. Each is optional and
 * ANDed with the rest; multi-value filters are ORed within themselves.
 */
export const v1AlertListQuerySchema = v1PaginationQuerySchema.extend({
    severity: csvParam('Keep only alerts at these severities.'),
    outcome: csvParam('Keep only alerts with these outcomes.'),
    /**
     * Filter on the finding kind (KIR-4432).
     *
     * Published because the Alerts screen offers this filter and the API did not,
     * so a customer could see a finding kind in the UI and had no way to narrow to
     * it. Named for the field it filters, per `NAMING.md`.
     *
     * Deliberately on `type` rather than on `title`: the screen's title filter
     * encodes a `<subPolicyKey>::<type>` composite, which is exactly the internal
     * notation this surface does not publish. Both halves of that pair are on
     * every alert, so a caller that needs title granularity filters on `type` and
     * partitions the page on `subPolicyKey` — no composite, and no reverse
     * label lookup that would break the moment a title is reworded.
     */
    type: csvParam('Keep only alerts of these finding kinds, as returned in `type`. To narrow further to a single title, also partition the results on `subPolicyKey` — one type can carry two titles.'),
    /** Kirin user ids, as returned in `users[].id`. */
    userId: csvParam('Keep only alerts seen for these Kirin user IDs, as returned in `users[].id`.'),
    /** Substring match on the alert's file path. */
    filePath: z
        .string()
        .min(1)
        .optional()
        .describe('Keep only alerts whose file path contains this substring.'),
    /** IDE / agent platform identifiers, as returned in `platforms[].id`. */
    platform: csvParam(`Keep only alerts seen on these IDEs or agent platforms. ${v1PlatformIdFilterDescription}`),
    /**
     * Date bounds are all **exclusive** — `firstSeenAfter` matches alerts first
     * seen strictly after the instant given. Exclusive in both directions, and on
     * both fields, so a caller paging a window by feeding the last result's
     * timestamp back in never re-reads the boundary row.
     */
    firstSeenAfter: z
        .string()
        .datetime()
        .optional()
        .describe('Keep only alerts first seen strictly after this instant (ISO 8601). Exclusive, so paging a window by feeding the last result back in never re-reads the boundary row.'),
    firstSeenBefore: z
        .string()
        .datetime()
        .optional()
        .describe('Keep only alerts first seen strictly before this instant (ISO 8601).'),
    lastSeenAfter: z
        .string()
        .datetime()
        .optional()
        .describe('Keep only alerts last seen strictly after this instant (ISO 8601).'),
    lastSeenBefore: z
        .string()
        .datetime()
        .optional()
        .describe('Keep only alerts last seen strictly before this instant (ISO 8601).'),
    /**
     * Which alerts to return. Defaults to `active`.
     *
     * `suppressed` returns an **empty page** for an organization that has neither
     * muting nor exclusions enabled. That is not an error: with no suppression
     * mechanism available, no alert can be suppressed, and the empty page is
     * indistinguishable from an organization that simply has nothing suppressed
     * right now. Enabling either feature makes the same query start returning
     * rows, with no client change.
     */
    suppressionState: v1AlertSuppressionStateFilterSchema
        .default('active')
        .describe('Which alerts to return, by suppression state. `suppressed` returns an empty page for an organization with neither muting nor exclusions enabled: with no suppression mechanism available, nothing can be suppressed.'),
    /**
     * Filter on the triage verdict (KIR-4432).
     *
     * Behind the same rule `suppressionState=suppressed` follows, and for the same
     * reason: the internal handler **drops** a verdict filter when
     * `alert_feedback` is off, which would turn a narrow question into the whole
     * unfiltered list. The route answers an **empty page** instead — with the
     * feature disabled no alert carries a verdict, so that is the truthful answer
     * as well as the safe one, and it leaks no flag state.
     */
    feedbackStatus: csvParam('Keep only alerts carrying these triage verdicts, as returned in `feedback.status` — one of `unverified`, `verified`, `false-positive`, `true-positive`, `unknown`. Returns an empty page when alert feedback is not enabled for the organization: with no verdicts recorded, nothing can match.'),
    sortBy: v1AlertSortFieldSchema.default('lastSeenAt'),
    sortOrder: v1SortOrderSchema.default('desc'),
});
export const v1AlertListResponseSchema = z
    .object({
    data: z.array(v1AlertSchema).describe('This page of alerts.'),
    pagination: v1PaginationSchema,
})
    .meta({
    id: 'V1AlertList',
    title: 'Alert list',
    description: 'A page of alerts, plus where it sits in the full result set.',
    examples: [
        {
            data: [
                {
                    id: '9e2aca8a1ba8912841bbf861767ac310a766435ee98324182237ba65d52b7150',
                    type: 'secret-leakage',
                    title: 'Secret detected in chat content',
                    severity: 'critical',
                    description: 'An AWS access key was sent to an AI assistant in chat content.',
                    explanation: {
                        whatHappened: 'An AWS access key ID and secret were pasted into a Claude Code chat.',
                        whyItMatters: 'Chat content leaves the machine. A live cloud credential in a prompt is a credential disclosed to a third party.',
                        detected: 'AWS access key ID (AKIA…), 1 occurrence',
                    },
                    // Same row as the V1Alert example above.
                    mitigation: 'Confirm whether the value is a real credential or a test value or placeholder. If it is real, check whether it also exists in a commit, an exported conversation, or a shared log, and revoke and reissue it if you cannot rule that out; a credential reliably blocked before transmission and not exposed elsewhere may not need rotation. If it is real, remove it from the content and keep it in your secret manager. If it is a test value or placeholder, make sure it is clearly marked as non-production.',
                    suppressionState: 'active',
                    outcome: 'detected',
                    feedback: null,
                    firstSeenAt: '2026-08-24T09:12:44Z',
                    lastSeenAt: '2026-08-31T14:03:07Z',
                    occurrenceCount: 3,
                    filePath: null,
                    score: 92,
                    policyKey: 'chat-security',
                    policyName: 'Chat & Tool-Call Security',
                    subPolicyKey: 'secret-detection',
                    subPolicyName: 'Secret Detection',
                    platforms: [{ id: 'claude', name: 'Claude Code' }],
                    users: [
                        {
                            id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
                            name: 'Stella Normal',
                            email: 'stella@example.com',
                            occurrenceCount: 3,
                            lastSeenAt: '2026-08-31T14:03:07Z',
                        },
                    ],
                    mute: null,
                    exclusion: null,
                },
            ],
            pagination: {
                page: 1,
                pageSize: 25,
                totalItems: 137,
                totalPages: 6,
                hasMore: true,
            },
        },
    ],
});
/**
 * A suppression deadline: an ISO instant strictly in the future, or null/absent
 * for "indefinitely".
 *
 * **Strictly** in the future, with no backwards tolerance — deliberately
 * stricter than the internal `muteAlertRequestSchema`, which forgives 5s of
 * client→server clock skew.
 *
 * That tolerance is right for the web app, whose picker only ever produces
 * deadlines minutes or hours out, so the slack purely absorbs drift on a value
 * that was always genuinely in the future. It is wrong here. Suppression is
 * active only while `until > now()`, evaluated when the row is read back, so a
 * deadline inside the tolerance window writes a row and then reports
 * `muted: false` / `excluded: false`. The caller gets a 200 that says the thing
 * it just asked for is not in effect, and cannot tell that from a no-op.
 *
 * Rejecting is strictly more useful than accepting there: the mute the caller
 * asked for has already elapsed either way, and a 400 naming the field says so.
 * With this rule a 2xx from a suppression write means the suppression is in
 * force. (A deadline a millisecond ahead can still expire in the round trip —
 * that race is inherent to any absolute-deadline API — but the guaranteed
 * multi-second dead zone is gone.)
 */
const futureOrNull = (field) => z
    .string()
    .datetime()
    .nullable()
    .optional()
    .refine(value => value === null ||
    value === undefined ||
    new Date(value).getTime() > Date.now(), {
    message: `${field} must be strictly in the future; a deadline that has already passed would write a suppression that is immediately inactive`,
});
/**
 * `POST .../alerts/:alertId/mute` body.
 *
 * Every field is optional so `{}` is a valid request — a new **required**
 * request field would be a breaking change, and the contract snapshot test
 * enforces that. Omitting `mutedUntil` means "mute indefinitely".
 */
export const v1MuteAlertRequestSchema = z
    .object({
    mutedUntil: futureOrNull('mutedUntil').describe('When the mute should expire (ISO 8601). Must be strictly in the future. Omit or send null to mute indefinitely.'),
})
    .meta({
    id: 'V1MuteAlertRequest',
    title: 'Mute request',
    description: 'Every field is optional, so `{}` is a valid request that mutes indefinitely. Safe to retry: muting an already-muted alert returns 200 with the mute currently in force rather than a conflict, and does not re-time it. To change an active window, remove the mute first.',
    // A deliberately far-off date, not a plausible one: the reference page
    // prefills its "Try it" body from here, and `futureOrNull` rejects anything
    // not strictly in the future — so a nearby date turns the documented
    // example into a 400 the moment it elapses.
    examples: [{ mutedUntil: '2099-12-31T23:59:59Z' }, {}],
});
/** `POST .../alerts/:alertId/exclusions` body. Mirror of the mute request. */
export const v1CreateAlertExclusionRequestSchema = z
    .object({
    excludedUntil: futureOrNull('excludedUntil').describe('When the exclusion should expire (ISO 8601). Must be strictly in the future. Omit or send null to exclude indefinitely.'),
})
    .meta({
    id: 'V1CreateAlertExclusionRequest',
    title: 'Exclusion request',
    description: 'Mirror of the mute request, with the same retry semantics: creating an exclusion on an already-excluded alert returns 200 with the exclusion in force.',
    examples: [{ excludedUntil: '2099-12-31T23:59:59Z' }, {}],
});
/**
 * What a mute / exclusion write returns: the alert id plus the resulting state.
 *
 * The state, not just an acknowledgement, because both writes are idempotent —
 * a retry that finds the alert already muted returns 200 with the state that is
 * actually in force, and a caller needs to be able to tell the two apart by
 * reading `mutedAt` rather than by the status code.
 */
export const v1AlertMuteResponseSchema = z
    .object({
    alertId: z.string().describe('The alert that was muted.'),
    mute: v1AlertMuteSchema.describe('The mute now in force.'),
})
    .meta({
    id: 'V1AlertMuteResult',
    title: 'Mute result',
    description: 'The resulting state rather than a bare acknowledgement, because the write is idempotent: read `mute.mutedAt` to tell a fresh mute from a replayed one.',
    examples: [
        {
            alertId: '9e2aca8a1ba8912841bbf861767ac310a766435ee98324182237ba65d52b7150',
            mute: {
                muted: true,
                mutedAt: '2026-08-31T15:41:02Z',
                mutedUntil: '2026-09-30T00:00:00Z',
                mutedBy: {
                    id: '3fb9c1d4-7e62-4a08-9c53-2d81f0a64e7b',
                    name: 'Roger Wilco',
                    email: 'roger@example.com',
                },
            },
        },
    ],
});
/**
 * `PUT .../alerts/:alertId/severity` body (KIR-5192). Unpublished: the route is
 * hidden from the document until the severity-override feature is released.
 */
export const v1SetAlertSeverityRequestSchema = z
    .object({
    severity: v1AlertSeveritySchema.describe('The severity the alert should report from now on, replacing the one Kirin assigned.'),
})
    .meta({
    id: 'V1SetAlertSeverityRequest',
    title: 'Set severity request',
    description: "Safe to retry: setting the severity an alert is already overridden to returns 200 and changes nothing. A `PUT` always pins the value, even one equal to the severity Kirin assigned, so later changes to Kirin's assessment do not apply until the override is removed with `DELETE`.",
    examples: [{ severity: 'high' }],
});
/**
 * What a severity write returns (KIR-5192): the alert id plus the severity the
 * alert now reports — the same value `GET .../alerts/:alertId` and the list
 * return for it. Who changed it, when, and the original severity are not
 * published (see NAMING.md).
 */
export const v1AlertSeverityResponseSchema = z
    .object({
    alertId: z.string().describe('The alert whose severity was written.'),
    severity: v1AlertSeveritySchema.describe('The severity the alert now reports.'),
})
    .meta({
    id: 'V1AlertSeverityResult',
    title: 'Severity result',
    description: 'The severity the alert reports after the write. After a reset this is the severity Kirin assigned.',
    examples: [
        {
            alertId: '9e2aca8a1ba8912841bbf861767ac310a766435ee98324182237ba65d52b7150',
            severity: 'high',
        },
    ],
});
export const v1AlertExclusionResponseSchema = z
    .object({
    alertId: z.string().describe('The alert that was excluded.'),
    exclusion: v1AlertExclusionSchema.describe('The exclusion now in force.'),
})
    .meta({
    id: 'V1AlertExclusionResult',
    title: 'Exclusion result',
    description: 'The resulting state rather than a bare acknowledgement. Read `exclusion.excludedAt` to tell a fresh exclusion from a replayed one.',
    examples: [
        {
            alertId: '9e2aca8a1ba8912841bbf861767ac310a766435ee98324182237ba65d52b7150',
            exclusion: {
                excluded: true,
                excludedAt: '2026-08-31T15:44:19Z',
                excludedUntil: null,
                excludedBy: {
                    id: '3fb9c1d4-7e62-4a08-9c53-2d81f0a64e7b',
                    name: 'Roger Wilco',
                    email: 'roger@example.com',
                },
            },
        },
    ],
});
//# sourceMappingURL=alerts.js.map